import { buildAccountKey } from '@sales-portal/domain/pricing/blocs/utils/account-key';
import { Dispatch, SetStateAction, useCallback, useRef } from 'react';
import { OnChangeFn, SortingState } from '@tanstack/react-table';
import { PricingGroupedTableRow } from '@sales-portal/domain/pricing/screens/management/types';

interface Params<T> {
  /** Stable identity shared by a row and its edit-merged copy. */
  keyOf: (row: T) => string;
  /** Edit-merged rows, one per key. */
  rows: T[];
  setSorting: Dispatch<SetStateAction<SortingState>>;
  /** Identity of the server rows; a new value re-takes the snapshot. */
  source: unknown;
}

/**
 * Feeds the column sort comparators from a snapshot instead of the live rows.
 *
 * Every edit invalidates the table's row model (edit-merged parents on the
 * grouped tables, a re-filtered array on the detail tables), so the table
 * re-sorts. Reading live values there would reorder rows under the user
 * mid-edit. The snapshot is re-taken only when the server rows change or when
 * the user re-sorts, so a sort reflects the edits made so far but no edit
 * moves a row on its own.
 */
export const usePricingSortSnapshot = <T>({
  keyOf,
  rows,
  setSorting,
  source,
}: Params<T>) => {
  const buildSnapshot = useCallback(
    () => new Map(rows.map((row) => [keyOf(row), row])),
    [keyOf, rows],
  );

  const snapshotRef = useRef<Map<string, T> | null>(null);
  const sourceRef = useRef(source);

  if (!snapshotRef.current || sourceRef.current !== source) {
    sourceRef.current = source;
    snapshotRef.current = buildSnapshot();
  }

  const onSortingChange = useCallback<OnChangeFn<SortingState>>(
    (updater) => {
      snapshotRef.current = buildSnapshot();
      setSorting(updater);
    },
    [buildSnapshot, setSorting],
  );

  const resolveEditedRow = useCallback(
    (row: T): T => snapshotRef.current?.get(keyOf(row)) ?? row,
    [keyOf],
  );

  return { onSortingChange, resolveEditedRow };
};

// A parent is not an account, so it keys by its group rather than by a pair
// key that could collide with one of its own leaves.
export const groupedRowKey = (row: PricingGroupedTableRow): string =>
  'subRows' in row ? `group:${row.groupKey}` : buildAccountKey(row);
